<script setup>
import TitleDescription from '@/components/InsuranceProgram/TitleDescription.vue';
import NoteButtons from '@/components/InsuranceProgram/NoteButtons.vue';
import Info from '@/components/InsuranceProgram/Info.vue';

defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const activeTab = ref('program-0');
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});
const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>
<template>
  <div
    :class="[
      backgroundColor
        ? `insurance-program insurance-program--${backgroundColor}`
        : 'insurance-program',
    ]"
  >
    <template v-if="data.categories && data.categories.length > 1">
      <div class="insurance-program__wrap">
        <p
          v-if="data?.title && data?.title.length > 0"
          class="insurance-program__title h3 mb-0"
          v-html="data?.title"
        ></p>
        <Tabs
          v-model:value="activeTab"
          :scrollable="true"
          class="insurance-program__tabs"
        >
          <TabList
            ref="tabList"
            :class="`insurance-program__tabs-headers tabs-bg-${data.background_color}`"
          >
            <Tab
              v-for="(tab, tabIndex) in data.categories"
              :key="tabIndex"
              :value="`program-${tabIndex}`"
              class="insurance-program__tabs-title"
            >
              {{ tab.title }}
            </Tab>
          </TabList>
          <TabPanels class="insurance-program__tabs-content">
            <TabPanel
              v-for="(tab, tabIndex) in data.categories"
              :key="tabIndex"
              :value="`program-${tabIndex}`"
              class="insurance-program__slider"
            >
              <div class="insurance-program__data">
                <TitleDescription
                  v-if="tab?.description"
                  :description="tab?.description"
                  class="insurance-program__top"
                />
                <div class="insurance-program__middle">
                  <Info
                    v-if="
                      (tab?.details && tab?.details.length > 0) ||
                      (tab?.advantages && tab?.advantages.length > 0)
                    "
                    :details="tab?.details"
                    :advantages="tab?.advantages"
                  />
                </div>
                <NoteButtons
                  class="insurance-program__bottom"
                  :note="tab?.note"
                  :buttons="tab?.buttons"
                />
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </template>
    <template v-else>
      <div class="insurance-program__data">
        <TitleDescription
          v-if="data?.title || data?.categories[0]?.description"
          :title="data?.title"
          :description="data?.categories[0]?.description"
          class="insurance-program__top"
        />
        <div class="insurance-program__middle">
          <Info
            v-if="
              (data?.categories[0]?.details &&
                data?.categories[0]?.details.length > 0) ||
              (data?.categories[0]?.advantages &&
                data?.categories[0]?.advantages.length > 0)
            "
            :details="data?.categories[0]?.details"
            :advantages="data?.categories[0]?.advantages"
          />
        </div>
        <NoteButtons
          class="insurance-program__bottom"
          :note="data?.categories[0]?.note"
          :buttons="data?.categories[0]?.buttons"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.insurance-program {
  $parent: &;
  border-radius: 8px;
  padding: 32px 16px;
  @include media-breakpoint-up(sm) {
    padding: 48px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(padding, 48);
  }
  &__wrap {
    display: flex;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 40);
    }
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-content) {
        border-bottom: 1px solid $divider;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-width, 1);
        }
      }
      &:deep(.p-tablist-tab-list) {
        gap: 8px;
        @include media-breakpoint-up(sm) {
          justify-content: center;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }

      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
    }
  }
  &__data {
    display: grid;
    gap: 32px;
    grid-template-columns: 1fr;
    grid-template-areas: 'top' 'middle' 'bottom';
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(md) {
      gap: 16px;
      grid-template-columns: 4.7fr 7.3fr;
      grid-template-areas: 'top middle' 'bottom middle';
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(gap, 16);
    }
  }
  &--white {
    background-color: $gray;
  }
  &--gray {
    background-color: $white;
  }
  &--black {
    background-color: rgba($surface-container-opacity, 0.5);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 16px;
    grid-area: top;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__middle {
    border-top: 1px solid $divider;
    grid-area: middle;
    padding-top: 32px;
    @include media-breakpoint-up(sm) {
      padding-top: 48px;
    }
    @include media-breakpoint-up(md) {
      border-left: 1px solid $divider;
      border-top: unset;
      padding-left: 48px;
      padding-top: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-left-width, 1);
      @include responsive-spacing(padding-left, 48);
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 32px;
    grid-area: bottom;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(md) {
      margin-top: auto;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
}
</style>
